import React, { useState, useRef } from "react";
import { Parcel, SoilZones } from "../types";
import { CLASS_BY_ID } from "../constants/landUseConfig";
import { pointsAttr } from "../utils/formatters";
import { polygonCentroid } from "../utils/voronoiGenerator";
import { Plus, Minus } from 'lucide-react';

interface Props {
  parcels: Parcel[];
  soilZones: SoilZones;
  selectedParcelId: string | null;
  changedByOptimizer: Set<string>;
  onParcelClick: (id: string) => void;
  onSoilToggle: (zone: "organic" | "mineral") => void;
}

export const LandUseMap: React.FC<Props> = ({
  parcels,
  soilZones,
  selectedParcelId,
  changedByOptimizer,
  onParcelClick,
  onSoilToggle,
}) => {
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const svgRef = useRef<SVGSVGElement>(null);

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.2, 2.5));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.2, 0.6));
  const handleResetZoom = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  // Mouse drag handlers
  const handleMouseDown = (e: React.MouseEvent<SVGSVGElement>) => {
    if ((e.target as SVGElement).tagName === 'polygon') return; // Allow parcel clicks
    setIsDragging(true);
    setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
  };

  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!isDragging) return;
    setPan({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  // Calculate viewBox based on zoom and pan
  const baseViewBox = { x: -70, y: -70, w: 2140, h: 2200 };
  const viewSize = { w: baseViewBox.w / zoom, h: baseViewBox.h / zoom };
  const panScale = 2 / zoom; // Scale pan movement based on zoom level
  const viewOffset = {
    x: baseViewBox.x + (baseViewBox.w - viewSize.w) / 2 - (pan.x * panScale),
    y: baseViewBox.y + (baseViewBox.h - viewSize.h) / 2 - (pan.y * panScale)
  };

  return (
<div className="bg-white border border-gray-300 rounded-xl shadow-md relative max-h-[600px] overflow-auto">      {/* Zoom Controls */}
      <div className="absolute top-3 right-3 z-10 flex flex-col gap-1 bg-white rounded-lg shadow-lg border border-gray-300 overflow-hidden">
        <button
          onClick={handleZoomIn}
          className="p-2 hover:bg-gray-100 transition-colors border-b border-gray-200"
          title="Zoom In"
        >
          <Plus className="w-4 h-4 text-gray-700" />
        </button>
        <button
          onClick={handleResetZoom}
          className="px-2 py-1 text-[10px] font-bold text-gray-700 hover:bg-gray-100 transition-colors border-b border-gray-200"
          title="Reset Zoom & Pan"
        >
          1:1
        </button>
        <button
          onClick={handleZoomOut}
          className="p-2 hover:bg-gray-100 transition-colors"
          title="Zoom Out"
        >
          <Minus className="w-4 h-4 text-gray-700" />
        </button>
      </div>

      <svg 
        ref={svgRef}
        viewBox={`${viewOffset.x} ${viewOffset.y} ${viewSize.w} ${viewSize.h}`}
        className={`w-full h-auto ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
        preserveAspectRatio="xMidYMid meet"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
      >
        <defs>
          <pattern
            id="hatchOrganic"
            width="14"
            height="14"
            patternTransform="rotate(45)"
            patternUnits="userSpaceOnUse"
          >
            <rect width="14" height="14" fill="transparent" />
            <line
              x1="0"
              y1="0"
              x2="0"
              y2="14"
              stroke="#EFE6C6"
              strokeWidth="3.2"
            />
          </pattern>
          <pattern
            id="hatchMineral"
            width="16"
            height="16"
            patternTransform="rotate(-45)"
            patternUnits="userSpaceOnUse"
          >
            <rect width="16" height="16" fill="transparent" />
            <circle cx="4" cy="4" r="1.7" fill="#E4E9E2" />
          </pattern>
        </defs>

        {/* Background */}
        <rect x="0" y="0" width="2000" height="2000" fill="#FBF9F2" />

        {/* Parcels */}
        <g>
          {parcels.map((p) => {
            const c = CLASS_BY_ID[p.cls];
            const isSelected = p.id === selectedParcelId;
            const isChanged = changedByOptimizer.has(p.id);

            return (
              <polygon
                key={p.id}
                points={pointsAttr(p.pts)}
                fill={c.fill}
                stroke={isSelected ? "#20281F" : c.ink}
                strokeWidth={isSelected ? 6 : isChanged ? 3.5 : 1.6}
                strokeDasharray={isChanged ? "6 4" : undefined}
                className="cursor-pointer hover:stroke-[4px] transition-all"
                onClick={() => onParcelClick(p.id)}
              />
            );
          })}
        </g>

        {/* Optimizer markers */}
        {parcels.map((p) => {
          if (!changedByOptimizer.has(p.id)) return null;
          const [cx, cy] = polygonCentroid(p.pts);
          return (
            <g key={`marker-${p.id}`} style={{ pointerEvents: "none" }}>
              <circle
                cx={cx}
                cy={cy}
                r={26}
                fill="#FBF9F2"
                stroke="#20281F"
                strokeWidth={2.5}
              />
              <text
                x={cx}
                y={cy + 8}
                textAnchor="middle"
                fontSize="30"
                fill="#20281F"
                fontWeight="700"
              >
                ★
              </text>
            </g>
          );
        })}

        {/* Soil overlays */}
        <g style={{ pointerEvents: "none" }}>
          <rect
            id="organicOverlay"
            x="0"
            y="0"
            width="1000"
            height="2000"
            opacity="0.55"
            fill={
              soilZones.organic === "organic"
                ? "url(#hatchOrganic)"
                : "url(#hatchMineral)"
            }
          />
          <rect
            id="mineralOverlay"
            x="1000"
            y="0"
            width="1000"
            height="2000"
            opacity="0.55"
            fill={
              soilZones.mineral === "mineral"
                ? "url(#hatchMineral)"
                : "url(#hatchOrganic)"
            }
          />
          <line
            x1="1000"
            y1="0"
            x2="1000"
            y2="2000"
            stroke="#20281F"
            strokeWidth="2"
            strokeDasharray="8 6"
          />
        </g>

        {/* Border */}
        <rect
          x="0"
          y="0"
          width="2000"
          height="2000"
          fill="none"
          stroke="#20281F"
          strokeWidth="3"
        />

        {/* Soil labels */}
        <g
          onClick={() => onSoilToggle("organic")}
          className="cursor-pointer"
          style={{ pointerEvents: "auto" }}
        >
          <text
            x="500"
            y="-24"
            textAnchor="middle"
            fontFamily="ui-monospace, monospace"
            fontSize="22"
            fill="#5C6B52"
          >
            SOIL: {soilZones.organic.toUpperCase()} (click)
          </text>
        </g>
        <g
          onClick={() => onSoilToggle("mineral")}
          className="cursor-pointer"
          style={{ pointerEvents: "auto" }}
        >
          <text
            x="1500"
            y="-24"
            textAnchor="middle"
            fontFamily="ui-monospace, monospace"
            fontSize="22"
            fill="#5C6B52"
          >
            SOIL: {soilZones.mineral.toUpperCase()} (click)
          </text>
        </g>

        {/* Scale bar */}
        <g transform="translate(30,1940)">
          <line
            x1="0"
            y1="0"
            x2="200"
            y2="0"
            stroke="#20281F"
            strokeWidth="4"
          />
          <line x1="0" y1="-8" x2="0" y2="8" stroke="#20281F" strokeWidth="4" />
          <line
            x1="200"
            y1="-8"
            x2="200"
            y2="8"
            stroke="#20281F"
            strokeWidth="4"
          />
          <text
            x="100"
            y="26"
            textAnchor="middle"
            fontFamily="ui-monospace, monospace"
            fontSize="20"
            fill="#20281F"
          >
            200 m
          </text>
        </g>

        {/* North arrow */}
        <g transform="translate(1940,60)">
          <polygon points="0,-26 10,10 0,0 -10,10" fill="#20281F" />
          <text
            x="0"
            y="34"
            textAnchor="middle"
            fontFamily="ui-monospace, monospace"
            fontSize="20"
            fill="#20281F"
          >
            N
          </text>
        </g>
      </svg>
    </div>
  );
};